import type { ProposalNodeSet, RfqPackage, TimberSupplierInvite, TimberSupplierStructureRef } from '../types';
import { packageScopeStructureNames } from './rfqPackageScopeNames';

/**
 * Client species selections from the proposal builder doc (kit-wide + per-structure).
 * `selectedSpecies` is a string for kit-wide picks, but the public sign flow writes
 * it as a structureId -> species map, so both shapes must be handled.
 */
export type ProposalSpeciesSelectionSource = {
  selectedSpecies?: string | Record<string, string>;
  speciesPricing?: {
    speciesName?: string;
    /** structureId -> species name chosen by the client. */
    selections?: Record<string, string>;
  };
};

function asTrimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Per-structure species map merged from `selectedSpecies` (map form) and `speciesPricing.selections`. */
function perStructureSelections(builder: ProposalSpeciesSelectionSource | null | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  const sources = [
    builder?.speciesPricing?.selections,
    typeof builder?.selectedSpecies === 'object' && builder.selectedSpecies !== null
      ? builder.selectedSpecies
      : undefined,
  ];
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue;
    for (const [structureId, species] of Object.entries(source)) {
      const trimmed = asTrimmedString(species);
      if (trimmed) out[structureId] = trimmed;
    }
  }
  return out;
}

/**
 * Loose key for structure-name matching across sources (proposal names vs
 * Cadwork CSV group names): case-insensitive, punctuation-insensitive, and
 * whitespace-collapsed, so "Entry-Lintels " matches "entry lintels".
 */
export function normalizeName(name: string | undefined): string {
  return (name ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** True when this invite limits the supplier to a subset of structures. */
export function inviteHasStructureScope(
  invite: Pick<TimberSupplierInvite, 'structureScope' | 'structures'> | undefined,
): boolean {
  return invite?.structureScope === 'selected' && (invite.structures?.length ?? 0) > 0;
}

/**
 * Node sets this supplier should receive. Matches by structure id first, then by
 * name — hub-CSV releases parse structures with generated ids but keep the
 * Cadwork group names, so name is the stable cross-source key.
 */
export function filterNodeSetsForInvite<T extends Pick<ProposalNodeSet, 'id' | 'name'>>(
  nodeSets: T[],
  invite: Pick<TimberSupplierInvite, 'structureScope' | 'structures'> | undefined,
): T[] {
  if (!inviteHasStructureScope(invite)) return nodeSets;
  const ids = new Set((invite!.structures ?? []).map(s => s.id).filter(Boolean));
  const names = new Set((invite!.structures ?? []).map(s => normalizeName(s.name)).filter(Boolean));
  return nodeSets.filter(ns => ids.has(ns.id) || names.has(normalizeName(ns.name)));
}

/**
 * Structure refs on a scoped invite that match none of the given node sets —
 * e.g. renamed structures, or a published timber list whose Cadwork group
 * names differ from the proposal structure names. These structures would be
 * silently dropped from the supplier's package, so surface them to staff.
 */
export function unmatchedStructureRefs(
  nodeSets: Array<Pick<ProposalNodeSet, 'id' | 'name'>>,
  invite: Pick<TimberSupplierInvite, 'structureScope' | 'structures'> | undefined,
): TimberSupplierStructureRef[] {
  if (!inviteHasStructureScope(invite)) return [];
  const ids = new Set(nodeSets.map(ns => ns.id).filter(Boolean));
  const names = new Set(nodeSets.map(ns => normalizeName(ns.name)).filter(Boolean));
  return (invite!.structures ?? []).filter(
    ref => !ids.has(ref.id) && !names.has(normalizeName(ref.name)),
  );
}

/** Distinct species on a structure's timber list, in first-seen order. */
function timberListSpecies(nodeSet: ProposalNodeSet): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of nodeSet.materials?.timberList ?? []) {
    const species = item.species?.trim();
    if (!species) continue;
    const key = species.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(species);
  }
  return out;
}

/**
 * Species this supplier should quote, derived from the proposal: the client's
 * per-structure selection when present, else the kit-wide selection, else the
 * species on that structure's timber list. Union across the supplier's
 * in-scope structures, sorted for display.
 */
export function proposalSpeciesForInvite(
  builder: ProposalSpeciesSelectionSource | null | undefined,
  nodeSets: ProposalNodeSet[],
  invite: Pick<TimberSupplierInvite, 'structureScope' | 'structures'> | undefined,
): string[] {
  const scoped = filterNodeSetsForInvite(
    nodeSets.filter(ns => ns.enabled !== false),
    invite,
  );
  const kitSpecies =
    asTrimmedString(builder?.selectedSpecies) || asTrimmedString(builder?.speciesPricing?.speciesName);
  const selections = perStructureSelections(builder);
  const set = new Map<string, string>();
  const add = (species: string) => {
    const trimmed = species.trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (!set.has(key)) set.set(key, trimmed);
  };
  for (const ns of scoped) {
    const clientChoice = selections[ns.id] ?? '';
    if (clientChoice) {
      add(clientChoice);
    } else if (kitSpecies) {
      add(kitSpecies);
    } else {
      for (const species of timberListSpecies(ns)) add(species);
    }
  }
  return Array.from(set.values()).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
}

export type ProposalSpeciesDrynessEntry = {
  species: string;
  drynessSpec: 'green' | 'kiln_dried';
};

function normalizeMoisture(raw: unknown): 'green' | 'kiln_dried' | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim().toLowerCase();
  if (s === 'green' || s === 'grn') return 'green';
  if (s === 'kiln_dried' || s === 'kiln' || s === 'kd') return 'kiln_dried';
  return null;
}

/** Green/KD tallies per species (lowercased key) across a supplier's in-scope structures. */
function moistureCountsForInvite(
  nodeSets: ProposalNodeSet[],
  invite: Pick<TimberSupplierInvite, 'structureScope' | 'structures'> | undefined,
): Map<string, { green: number; kiln: number }> {
  const scoped = filterNodeSetsForInvite(
    nodeSets.filter(ns => ns.enabled !== false),
    invite,
  );
  const counts = new Map<string, { green: number; kiln: number }>();
  const tally = (itemSpecies: string | undefined, moisture: unknown) => {
    const key = (itemSpecies ?? '').trim().toLowerCase();
    if (!key) return;
    const m = normalizeMoisture(moisture);
    if (!m) return;
    const row = counts.get(key) ?? { green: 0, kiln: 0 };
    if (m === 'green') row.green += 1;
    else row.kiln += 1;
    counts.set(key, row);
  };
  for (const ns of scoped) {
    for (const item of ns.materials?.timberList ?? []) {
      tally(item.species, item.moistureContent);
    }
    for (const alt of ns.materials?.alternateSpecies ?? []) {
      for (const item of alt.timberList ?? []) {
        tally(item.species?.trim() ? item.species : alt.speciesName, item.moistureContent);
      }
    }
  }
  return counts;
}

/**
 * Species + dryness this supplier should quote, both derived from the proposal.
 * Dryness per species comes from the moisture content on the in-scope structures'
 * timber lists (default and alternate-species lists) — majority wins, and
 * kiln-dried is the default when unspecified or tied. Mixed KD/GRN within one
 * species stays accurate at the package line-item level (see supplierTimberExport);
 * use {@link proposalMixedDrynessSpecies} to surface those in the UI.
 */
export function proposalSpeciesDrynessForInvite(
  builder: ProposalSpeciesSelectionSource | null | undefined,
  nodeSets: ProposalNodeSet[],
  invite: Pick<TimberSupplierInvite, 'structureScope' | 'structures'> | undefined,
): ProposalSpeciesDrynessEntry[] {
  const species = proposalSpeciesForInvite(builder, nodeSets, invite);
  if (!species.length) return [];
  const counts = moistureCountsForInvite(nodeSets, invite);
  return species.map(sp => {
    const row = counts.get(sp.toLowerCase());
    return {
      species: sp,
      drynessSpec: row && row.green > row.kiln ? 'green' : 'kiln_dried',
    };
  });
}

/** Species with both green and kiln-dried lines across the supplier's in-scope structures. */
export function proposalMixedDrynessSpecies(
  builder: ProposalSpeciesSelectionSource | null | undefined,
  nodeSets: ProposalNodeSet[],
  invite: Pick<TimberSupplierInvite, 'structureScope' | 'structures'> | undefined,
): string[] {
  const species = proposalSpeciesForInvite(builder, nodeSets, invite);
  if (!species.length) return [];
  const counts = moistureCountsForInvite(nodeSets, invite);
  return species.filter(sp => {
    const row = counts.get(sp.toLowerCase());
    return !!row && row.green > 0 && row.kiln > 0;
  });
}

/** Structure refs (id + name) from proposal node sets, for storing on an invite. */
export function structureRefsFromNodeSets(
  nodeSets: Pick<ProposalNodeSet, 'id' | 'name'>[],
): TimberSupplierStructureRef[] {
  return nodeSets.map(ns => ({ id: ns.id, name: ns.name }));
}

/** Badge like "2 of 5 structures" for a structure-scoped supplier; null when unscoped. */
export function structureScopeBadgeLabel(
  invite: Pick<TimberSupplierInvite, 'structureScope' | 'structures'> | undefined,
  totalStructures: number,
): string | null {
  if (!inviteHasStructureScope(invite)) return null;
  const count = invite!.structures!.length;
  if (totalStructures > 0) {
    return `${count} of ${totalStructures} structure${totalStructures === 1 ? '' : 's'}`;
  }
  return `${count} structure${count === 1 ? '' : 's'}`;
}

type StructureScopePkg = Pick<
  RfqPackage,
  'scopeStructures' | 'archivedStructureIds' | 'proposalSnapshot' | 'cncPackagePayload' | 'workType'
>;

function formatStructureCountLabel(count: number, totalStructures: number): string {
  if (totalStructures > 0) {
    return `${count} of ${totalStructures} structure${totalStructures === 1 ? '' : 's'}`;
  }
  return `${count} structure${count === 1 ? '' : 's'}`;
}

/**
 * Structure badge for a supplier card — includes selected-scope invites, all-scope
 * invites, and structure counts recorded on the published package.
 */
export function supplierStructureScopeBadgeLabel(
  invite: Pick<TimberSupplierInvite, 'structureScope' | 'structures'> | undefined,
  totalStructures: number,
  pkg?: StructureScopePkg,
): string | null {
  // The card is about the request you're looking at. Archived structures stay
  // on the original scope but no longer count as open.
  if (pkg?.scopeStructures?.length) {
    const openCount = packageScopeStructureNames(pkg).length;
    const total = totalStructures > 0 ? totalStructures : pkg.scopeStructures.length;
    return formatStructureCountLabel(openCount, total);
  }

  if (inviteHasStructureScope(invite)) {
    return structureScopeBadgeLabel(invite, totalStructures);
  }

  const pkgNames = pkg ? packageScopeStructureNames(pkg) : [];
  const pkgHasExplicitScope = !!(
    pkg?.scopeStructures?.length || pkg?.cncPackagePayload?.structures?.length
  );

  if (pkgHasExplicitScope && pkgNames.length > 0) {
    return formatStructureCountLabel(
      pkgNames.length,
      totalStructures > 0 ? totalStructures : pkgNames.length,
    );
  }

  if (invite?.structureScope === 'all' && totalStructures > 0) {
    return formatStructureCountLabel(totalStructures, totalStructures);
  }

  if (pkg && totalStructures > 0) {
    return formatStructureCountLabel(totalStructures, totalStructures);
  }

  return null;
}
